<template>
  <div>
    <v-card>
      <!-- CARD TITLE -->
      <v-card-title class="mb-0 pb-0">
        <h1 class="title mb-2">
          Truck Request #{{ truckRequest.orderNumber
          }}<status-chip class="ml-4" :status="truckRequest.status" />
          <v-icon
            v-if="showClose"
            style="position:absolute;right:16px"
            @click="closeClicked()"
            >close</v-icon
          >
          <div class="grey--text font-weight-regular body-2">
            {{ truckRequest.orderDate.toDate() | moment("MMM Do [at] h:mma") }}
          </div>
          <div
            class="font-weight-regular body-2"
            v-if="truckRequest.jobSite.client"
          >
            {{ truckRequest.jobSite.client.name }}
          </div>
        </h1>
        <h3
          class="red--text mt-4"
          v-if="
            truckRequest.status == 'closed' &&
              !truckRequest.dumpTickets &&
              truckRequest.requestType !== 'Mini Pickup/Dropoff'
          "
        >
          *** MISSING DUMP TICKET ***
        </h3>
      </v-card-title>
      <!-- CARD BODY -->
      <v-card-text class="pt-4">
        <!-- JOB DETAILS -->
        <v-simple-table dense>
          <template v-slot:default>
            <tbody>
              <tr>
                <td class="px-0 pl-1 mt-8 grey lighten-3 py-1" colspan="2">
                  <strong
                    >REQUEST DETAILS
                    <v-btn
                      class="ml-4"
                      color="primary"
                      text
                      outlined
                      x-small
                      v-if="
                        !editingTickets &&
                          (['Truck Manager', 'Yard Manager'].includes(
                            user.role
                          ) ||
                            user.isAdmin)
                      "
                      @click="startEditing()"
                      >edit</v-btn
                    >
                    <v-btn
                      class="ml-4"
                      color="primary"
                      text
                      outlined
                      x-small
                      v-if="editingTickets"
                      @click="editingTickets = false"
                      >cancel</v-btn
                    >
                    <v-btn
                      class="ml-2"
                      color="primary"
                      x-small
                      v-if="editingTickets"
                      @click="saveTruckRequest()"
                      >save</v-btn
                    >
                  </strong>
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1" width="120">ADDRESS</td>
                <td class="px-0 pr-1">
                  {{ truckRequest.jobSite.street }}
                  <span
                    class="font-weight-regular"
                    v-if="truckRequest.jobSite.street2"
                  >
                    {{ truckRequest.jobSite.street2 }}</span
                  >{{ truckRequest.jobSite.city }}
                  {{ truckRequest.jobSite.postalCode }}
                </td>
              </tr>
              <tr v-if="isCongestionZone">
                <td class="px-0 pr-1">CONGESTION ZONE</td>
                <td class="px-0 pr-1">
                  <v-chip small color="orange" text-color="white">
                    Yes - Congestion Zone
                  </v-chip>
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1">REQUEST DATE</td>
                <td class="px-0 pr-1">
                  <div v-if="editingTickets">
                    <v-menu
                      ref="menu"
                      v-model="dateMenu"
                      :close-on-content-click="false"
                      :return-value.sync="truckRequest.requestDate"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="requestDateFormatted"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          dense
                          hide-details
                          style="max-width:200px"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="truckRequest.requestDate"
                        scrollable
                        color="primary"
                      >
                        <v-spacer></v-spacer>
                        <v-btn
                          color="primary"
                          @click="$refs.menu.save(truckRequest.requestDate)"
                        >
                          OK
                        </v-btn>
                      </v-date-picker>
                    </v-menu>
                  </div>
                  <div v-else>
                    {{ truckRequest.requestDate | moment("MMM Do")
                    }}<span v-if="truckRequest.requestTime">
                      at {{ truckRequest.requestTime }}</span
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1">REQUEST TIME</td>
                <td class="px-0 pr-1">
                  <div v-if="editingTickets">
                    <v-select
                      style="font-size: 14px;max-width:200px"
                      :items="timeOptions"
                      v-model="truckRequest.requestTime"
                      dense
                      hide-details
                    ></v-select>
                  </div>
                  <div v-else>
                    {{ truckRequest.requestTime }}
                  </div>
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1">REQUESTED BY</td>
                <td class="px-0 pr-1">
                  {{ truckRequest.orderedBy.firstName }}
                  {{ truckRequest.orderedBy.lastName }}
                  - {{ truckRequest.orderedBy.phone }}
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1">REQUEST TYPE</td>
                <td class="px-0 pr-1">
                  <div v-if="editingTickets">
                    <v-select
                      style="font-size: 14px;max-width:200px"
                      :items="['Packer', 'Roll Off', 'Mini Pickup/Dropoff']"
                      v-model="truckRequest.requestType"
                      dense
                      hide-details
                      @change="requestTypeChanged"
                    ></v-select>
                  </div>
                  <div v-else>
                    {{ truckRequest.requestType }}
                  </div>
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1">PICKUP TYPE</td>
                <td class="px-0 pr-1">
                  <div v-if="editingTickets">
                    <v-select
                      style="font-size: 14px;max-width:200px"
                      :items="pickupTypeOptions"
                      v-model="truckRequest.pickupType"
                      dense
                      hide-details
                    ></v-select>
                  </div>
                  <div v-else>
                    {{ truckRequest.pickupType }}
                  </div>
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1">MATERIAL</td>
                <td class="px-0 pr-1">
                  {{
                    truckRequest.materialTypeBreakdown &&
                    truckRequest.materialTypeBreakdown.length
                      ? truckRequest.materialTypeBreakdown
                          .map((m) => m.material)
                          .join(", ")
                      : truckRequest.materialType
                      ? truckRequest.materialType.join(", ")
                      : ""
                  }}
                </td>
              </tr>
              <tr v-if="truckRequest.requestType !== 'Mini Pickup/Dropoff'">
                <td class="px-0 pr-1">QUANTITY</td>
                <td class="px-0 pr-1">
                  <div
                    v-if="editingTickets"
                    style="display: flex; align-items: center;"
                  >
                    <v-text-field
                      style="font-size: 14px;max-width:80px;"
                      class="mr-2"
                      v-model.number="truckRequest.qty"
                      type="number"
                      dense
                      hide-details
                    ></v-text-field>
                    <span>{{ truckRequest.qtyType }}</span>
                  </div>
                  <div v-else>
                    {{ truckRequest.qty || "" }}
                    {{ truckRequest.qtyType || "" }}
                  </div>
                </td>
              </tr>
              <tr v-if="truckRequest.requestType === 'Mini Pickup/Dropoff'">
                <td class="px-0 pr-1">MINI'S</td>
                <td class="px-0 pr-1">
                  <div
                    v-if="editingTickets"
                    style="display: flex; align-items: center; gap: 10px;"
                  >
                    <div>
                      Pickup
                      <v-text-field
                        style="font-size: 14px;max-width:80px;"
                        v-model.number="truckRequest.miniPickupQty"
                        type="number"
                        dense
                        hide-details
                      ></v-text-field>
                    </div>
                    <div>
                      Drop Off
                      <v-text-field
                        style="font-size: 14px;max-width:80px;"
                        v-model.number="truckRequest.miniDropoffQty"
                        type="number"
                        dense
                        hide-details
                      ></v-text-field>
                    </div>
                  </div>
                  <div v-else>
                    <span v-if="truckRequest.miniPickupQty"
                      >Pickup
                      <strong style="border-bottom: solid 1px gray;">{{
                        Math.abs(truckRequest.miniPickupQty)
                      }}</strong></span
                    >
                    <span
                      v-if="truckRequest.miniPickupQty && truckRequest.miniDropoffQty"
                    >
                      &
                    </span>
                    <span v-if="truckRequest.miniDropoffQty"
                      >Drop Off
                      <strong style="border-bottom: solid 1px gray;">{{
                        Math.abs(truckRequest.miniDropoffQty)
                      }}</strong></span
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1">DURATION</td>
                <td class="px-0 pr-1">
                  <div v-if="editingTickets">
                    <v-select
                      style="font-size: 14px;max-width:200px"
                      :items="durationOptions"
                      v-model="truckRequest.duration"
                      dense
                      hide-details
                    ></v-select>
                  </div>
                  <div v-else>
                    {{ truckRequest.duration }}
                  </div>
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1" style="vertical-align:top">NOTES</td>
                <td class="px-0 pr-1">
                  <div v-if="editingTickets">
                    <v-textarea
                      style="font-size: 14px;max-width:300px"
                      v-model="truckRequest.notes"
                      dense
                      hide-details
                      rows="3"
                    ></v-textarea>
                  </div>
                  <div v-else>
                    {{ truckRequest.notes }}
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <v-simple-table dense v-if="truckRequest.scheduledDay">
          <template v-slot:default>
            <tbody>
              <tr>
                <td class="px-0 pl-1 mt-8 grey lighten-3 py-1" colspan="2">
                  <strong>SCHEDULING DETAILS</strong>
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1" width="120">DAY</td>
                <td class="px-0 pr-1">
                  <div v-if="editingTickets">
                    <v-menu
                      ref="scheduledMenu"
                      v-model="scheduledDateMenu"
                      :close-on-content-click="false"
                      :return-value.sync="truckRequest.scheduledDay.day"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="scheduledDateFormatted"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          dense
                          hide-details
                          style="max-width:200px"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="truckRequest.scheduledDay.day"
                        scrollable
                        color="primary"
                        @change="updateScheduledDay"
                      >
                        <v-spacer></v-spacer>
                        <v-btn
                          color="primary"
                          @click="
                            $refs.scheduledMenu.save(
                              truckRequest.scheduledDay.day
                            )
                          "
                        >
                          OK
                        </v-btn>
                      </v-date-picker>
                    </v-menu>
                  </div>
                  <div v-else>
                    {{ truckRequest.scheduledDay.displayDate }}
                  </div>
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1">TIME</td>
                <td class="px-0 pr-1">
                  <div v-if="editingTickets">
                    <v-select
                      style="font-size: 14px;max-width:200px"
                      :items="timeOptions"
                      v-model="truckRequest.scheduledTime"
                      dense
                      hide-details
                    ></v-select>
                  </div>
                  <div v-else>
                    {{ truckRequest.scheduledTime }}
                  </div>
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1">DURATION</td>
                <td class="px-0 pr-1">
                  <div v-if="editingTickets">
                    <v-select
                      style="font-size: 14px;max-width:200px"
                      :items="durationOptions"
                      v-model="truckRequest.scheduledDuration"
                      dense
                      hide-details
                    ></v-select>
                  </div>
                  <div v-else>
                    {{ truckRequest.scheduledDuration }}
                  </div>
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1">DRIVER</td>
                <td class="px-0 pr-1">
                  <div v-if="editingTickets">
                    <v-select
                      style="font-size: 14px;max-width:200px"
                      :items="drivers"
                      v-model="truckRequest.scheduledDriver"
                      return-object
                      item-text="name"
                      dense
                      hide-details
                    ></v-select>
                  </div>
                  <div v-else>
                    {{ truckRequest.scheduledDriver.firstName }}
                    {{ truckRequest.scheduledDriver.lastName }}
                  </div>
                </td>
              </tr>
              <tr v-if="['Truck Manager', 'Driver'].includes(user.role)">
                <td class="px-0 pr-1">NOTES</td>
                <td class="px-0 pr-1">
                  <div v-if="editingTickets">
                    <v-textarea
                      style="font-size: 14px;max-width:300px"
                      v-model="truckRequest.truckManagerNotes"
                      dense
                      hide-details
                      rows="3"
                    ></v-textarea>
                  </div>
                  <div v-else>
                    {{ truckRequest.truckManagerNotes }}
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <v-simple-table dense v-if="truckRequest.closeDate">
          <template v-slot:default>
            <tbody>
              <tr>
                <td class="px-0 pl-1 mt-8 grey lighten-3 py-1" colspan="2">
                  <strong>CLOSING DETAILS</strong>
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1" width="120">CLOSE DATE</td>
                <td class="px-0 pr-1">
                  <div v-if="editingTickets">
                    <v-menu
                      ref="closeDateMenu"
                      v-model="closeDateMenu"
                      :close-on-content-click="false"
                      :return-value.sync="closeDateFormatted"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="closeDateFormatted"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          dense
                          hide-details
                          style="max-width:200px"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="closeDateFormatted"
                        scrollable
                        color="primary"
                        @change="updateCloseDate"
                      >
                        <v-spacer></v-spacer>
                        <v-btn
                          color="primary"
                          @click="$refs.closeDateMenu.save(closeDateFormatted)"
                        >
                          OK
                        </v-btn>
                      </v-date-picker>
                    </v-menu>
                  </div>
                  <div v-else>
                    {{ truckRequest.closeDate.toDate() | moment("MM/DD/YYYY") }}
                  </div>
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1">CLOSED BY</td>
                <td class="px-0 pr-1">
                  {{ truckRequest.closedBy.firstName }}
                  {{ truckRequest.closedBy.lastName }}
                </td>
              </tr>
              <tr v-if="truckRequest.requestType == 'Mini Pickup/Dropoff'">
                <td class="px-0 pr-1">MINI'S</td>
                <td class="px-0 pr-1">
                  <div
                    v-if="editingTickets"
                    style="display: flex; align-items: center; gap: 10px;"
                  >
                    <div>
                      Pickup
                      <v-text-field
                        style="font-size: 14px;max-width:80px;"
                        v-model.number="truckRequest.closingMiniPickupQty"
                        type="number"
                        dense
                        hide-details
                      ></v-text-field>
                    </div>
                    <div>
                      Drop Off
                      <v-text-field
                        style="font-size: 14px;max-width:80px;"
                        v-model.number="truckRequest.closingMiniDropoffQty"
                        type="number"
                        dense
                        hide-details
                      ></v-text-field>
                    </div>
                  </div>
                  <div v-else>
                    <span v-if="getClosingMiniPickupQty(truckRequest)"
                      >Pickup
                      <strong style="border-bottom: solid 1px gray;">{{
                        getClosingMiniPickupQty(truckRequest)
                      }}</strong></span
                    >
                    <span
                      v-if="
                        getClosingMiniPickupQty(truckRequest) &&
                          getClosingMiniDropoffQty(truckRequest)
                      "
                    >
                      &
                    </span>
                    <span v-if="getClosingMiniDropoffQty(truckRequest)"
                      >Drop Off
                      <strong style="border-bottom: solid 1px gray;">{{
                        getClosingMiniDropoffQty(truckRequest)
                      }}</strong></span
                    >
                  </div>
                </td>
              </tr>
              <!-- OLD QUANTITY SYSTEM (backwards compatibility) -->
              <tr
                v-if="
                  (truckRequest.closingQty || editingTickets) &&
                    !hasQuantityBreakdown
                "
              >
                <td class="px-0 pr-1">{{ truckRequest.qtyType }}</td>
                <td class="px-0 pr-1">
                  <div v-if="editingTickets">
                    <v-text-field
                      style="font-size: 14px;max-width:120px;"
                      v-model.number="truckRequest.closingQty"
                      type="number"
                      dense
                      hide-details
                    ></v-text-field>
                  </div>
                  <div v-else>
                    {{ truckRequest.closingQty }}
                  </div>
                </td>
              </tr>

              <!-- NEW QUANTITY BREAKDOWN SYSTEM -->
              <!-- Packer close-out only. Roll Off quantity is the container
                   size (truckRequest.qty), which the driver never re-enters,
                   so editing a Roll Off here used to save an all-zero
                   breakdown and blank out the Dump Log container size. -->
              <template
                v-if="
                  hasQuantityBreakdown ||
                    (editingTickets && truckRequest.requestType === 'Packer')
                "
              >
                <tr>
                  <td
                    class="px-0 pr-1"
                    style="vertical-align: top; padding-top: 12px; padding-bottom: 12px;"
                  >
                    QUANTITIES
                  </td>
                  <td
                    class="px-0 pr-1"
                    style="padding-top: 12px; padding-bottom: 12px;"
                  >
                    <div v-if="editingTickets">
                      <div
                        style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; max-width: 400px;"
                      >
                        <v-text-field
                          style="font-size: 12px;"
                          v-model.number="
                            editDriverQuantityBreakdown.looseYardsRegular
                          "
                          type="number"
                          label="Loose Yards Regular"
                          dense
                          hide-details
                          outlined
                          min="0"
                          step="0.1"
                        ></v-text-field>
                        <v-text-field
                          style="font-size: 12px;"
                          v-model.number="
                            editDriverQuantityBreakdown.looseYardsHeavy
                          "
                          type="number"
                          label="Loose Yards Heavy"
                          dense
                          hide-details
                          outlined
                          min="0"
                          step="0.1"
                        ></v-text-field>
                        <v-text-field
                          style="font-size: 12px;"
                          v-model.number="
                            editDriverQuantityBreakdown.minisRegular
                          "
                          type="number"
                          label="Minis Regular"
                          dense
                          hide-details
                          outlined
                          min="0"
                          step="1"
                        ></v-text-field>
                        <v-text-field
                          style="font-size: 12px;"
                          v-model.number="
                            editDriverQuantityBreakdown.minisHeavy
                          "
                          type="number"
                          label="Minis Heavy"
                          dense
                          hide-details
                          outlined
                          min="0"
                          step="1"
                        ></v-text-field>
                      </div>
                    </div>
                    <div v-else>
                      <div v-if="hasQuantityBreakdown">
                        <div>
                          <strong>Loose Yards Regular:</strong>
                          {{
                            truckRequest.driverQuantityBreakdown
                              .looseYardsRegular || 0
                          }}
                        </div>
                        <div>
                          <strong>Loose Yards Heavy:</strong>
                          {{
                            truckRequest.driverQuantityBreakdown
                              .looseYardsHeavy || 0
                          }}
                        </div>
                        <div>
                          <strong>Minis Regular:</strong>
                          {{
                            truckRequest.driverQuantityBreakdown.minisRegular ||
                              0
                          }}
                        </div>
                        <div>
                          <strong>Minis Heavy:</strong>
                          {{
                            truckRequest.driverQuantityBreakdown.minisHeavy || 0
                          }}
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr v-if="truckRequest.driverMaterialTypes || editingTickets">
                  <td class="px-0 pr-1" style="vertical-align: top;">
                    MATERIALS
                  </td>
                  <td class="px-0 pr-1">
                    <div v-if="editingTickets">
                      <v-chip-group
                        v-model="editDriverMaterialTypes"
                        column
                        multiple
                      >
                        <v-chip filter outlined value="C&D" small>
                          C&D
                        </v-chip>
                        <v-chip filter outlined value="Metal" small>
                          Metal
                        </v-chip>
                        <v-chip filter outlined value="Masonry/Dirt" small>
                          Masonry/Dirt
                        </v-chip>
                        <v-chip filter outlined value="Trees/Grass" small>
                          Trees/Grass
                        </v-chip>
                      </v-chip-group>
                    </div>
                    <div v-else>
                      <div
                        v-if="
                          truckRequest.driverMaterialTypes &&
                            truckRequest.driverMaterialTypes.length
                        "
                      >
                        <v-chip
                          v-for="material in truckRequest.driverMaterialTypes"
                          :key="material"
                          small
                          outlined
                          class="mr-1 mb-1"
                        >
                          {{ material }}
                        </v-chip>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
              <tr v-if="truckRequest.closeNotes || editingTickets">
                <td class="px-0 pr-1">NOTES</td>
                <td class="px-0 pr-1">
                  <div v-if="editingTickets">
                    <v-textarea
                      style="font-size: 14px;max-width:300px"
                      v-model="truckRequest.closeNotes"
                      dense
                      hide-details
                      rows="3"
                    ></v-textarea>
                  </div>
                  <div v-else>
                    {{ truckRequest.closeNotes }}
                  </div>
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1">NAME</td>
                <td class="px-0 pr-1">
                  <div v-if="editingTickets">
                    <v-text-field
                      style="font-size: 14px;max-width:200px"
                      v-model="truckRequest.clientPrintName"
                      dense
                      hide-details
                    ></v-text-field>
                  </div>
                  <div v-else>
                    {{ truckRequest.clientPrintName }}
                  </div>
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1" style="vertical-align:top">SIGNATURE</td>
                <td class="px-0 pr-1">
                  <v-img
                    :src="truckRequest.clientSignatureImage"
                    contain
                    max-width="200"
                  />
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <!-- DUMP TICKET -->
        <h3
          class="red--text mt-4"
          v-if="
            truckRequest.status == 'closed' &&
              !truckRequest.dumpTickets &&
              truckRequest.requestType !== 'Mini Pickup/Dropoff'
          "
        >
          *** MISSING DUMP TICKET ***
        </h3>
        <v-simple-table
          dense
          v-for="(value, key) in truckRequest.dumpTickets"
          :key="key"
        >
          <template v-slot:default>
            <tbody>
              <tr>
                <td class="px-0 pl-1 mt-8 grey lighten-3 py-1" colspan="2">
                  <strong>DUMP TICKET</strong>
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1" width="120">TICKET #</td>
                <td class="px-0 pr-1">
                  <div v-if="editingTickets">
                    <v-text-field
                      style="font-size: 14px;max-width:200px"
                      v-model="value.ticketNumber"
                      type="text"
                      hide-details
                      dense
                    ></v-text-field>
                  </div>
                  <div v-else>{{ value.ticketNumber }}</div>
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1" width="120">DUMP SITE</td>
                <td class="px-0 pr-1">
                  <div v-if="editingTickets">
                    <v-select
                      style="font-size: 14px;max-width:200px"
                      :items="[
                        'JD Recycling',
                        'Cooper Tank',
                        'Allocco recycling',
                        'Benson Scrap Metal',
                        'Deer Park Recycling',
                        'Point Recycling',
                        'Regal Recycling',
                        'Richmond Recycling',
                        'Zevel Transfer',
                        'TNT Scrap',
                        'Sals Scrap',
                        'Gaeta Recycling',
                        'Boro-Wide Recycling',
                        'AJ Recycling',
                        'Sims metal',
                        'America Recycling',
                        'Other',
                      ]"
                      v-model="value.dumpSite"
                      dense
                      hide-details
                    ></v-select>
                  </div>
                  <div v-else>
                    {{ value.dumpSite }}
                  </div>
                </td>
              </tr>
              <!-- <tr>
              <td class="px-0 pr-1">PRICE</td>
              <td class="px-0 pr-1">${{ value.price }}</td>
            </tr> -->
              <tr>
                <td class="px-0 pr-1">LOAD WEIGHT</td>
                <td
                  class="px-0 pr-1"
                  :style="
                    editingTickets
                      ? { marginRight: '10px', display: 'flex' }
                      : {}
                  "
                >
                  <span v-if="editingTickets">
                    <v-text-field
                      style="font-size: 14px;max-width:80px;"
                      class="mr-2"
                      v-model.number="value.loadWeight"
                      type="number"
                      dense
                    ></v-text-field>
                  </span>
                  <span v-else>{{ value.loadWeight }} </span>
                  <span v-if="editingTickets">
                    <v-select
                      style="font-size: 14px;max-width:100px"
                      :items="['Tons', 'Cubic Yards', 'Pounds']"
                      v-model="value.loadWeightType"
                      type="text"
                      dense
                    ></v-select>
                  </span>
                  <span v-else>{{ value.loadWeightType }}</span>
                </td>
              </tr>
              <tr>
                <td class="px-0 pr-1" style="vertical-align:top">IMAGE</td>
                <td class="px-0 pr-1">
                  <span v-for="image in value.dumpTicketImage" :key="image">
                    <a :href="image" target="_blank">
                      <img
                        style="border: solid 1px #ececec"
                        class="mr-2 mb-2 pa-1"
                        :src="
                          image.replace(
                            'https://firebasestorage.googleapis.com/',
                            'https://ik.imagekit.io/albademo/tr:w-100/'
                          )
                        "
                      />
                    </a>
                  </span>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
          {{ snackText }}
          <v-btn text @click="snack = false">Close</v-btn>
        </v-snackbar>
      </v-card-text>
    </v-card>
  </div>
</template>
<script>
import statusChip from "@/components/StatusChip";
import { mapState } from "vuex";
import fb from "@/main";
import moment from "moment";
import { sortBy } from "lodash";
import { isInCongestionZone } from "@/helpers/congestionZones";
export default {
  components: {
    statusChip,
  },
  props: ["truckRequest", "showClose"],
  data() {
    return {
      ticketOption: null,
      editingTickets: false,
      snack: false,
      snackColor: "",
      snackText: "",
      dateMenu: false,
      scheduledDateMenu: false,
      closeDateMenu: false,
      closeDateFormatted: "",
      drivers: [],
      timeOptions: [
        "12:15am",
        "12:30am",
        "12:45am",
        "1:00am",
        "1:15am",
        "1:30am",
        "1:45am",
        "2:00am",
        "2:15am",
        "2:30am",
        "2:45am",
        "3:00am",
        "3:15am",
        "3:30am",
        "3:45am",
        "4:00am",
        "4:15am",
        "4:30am",
        "4:45am",
        "5:00am",
        "5:15am",
        "5:30am",
        "5:45am",
        "6:00am",
        "6:15am",
        "6:30am",
        "6:45am",
        "7:00am",
        "7:15am",
        "7:30am",
        "7:45am",
        "8:00am",
        "8:15am",
        "8:30am",
        "8:45am",
        "9:00am",
        "9:15am",
        "9:30am",
        "9:45am",
        "10:00am",
        "10:15am",
        "10:30am",
        "10:45am",
        "11:00am",
        "11:15am",
        "11:30am",
        "11:45am",
        "12:00pm",
        "12:15pm",
        "12:30pm",
        "12:45pm",
        "1:00pm",
        "1:15pm",
        "1:30pm",
        "1:45pm",
        "2:00pm",
        "2:15pm",
        "2:30pm",
        "2:45pm",
        "3:00pm",
        "3:15pm",
        "3:30pm",
        "3:45pm",
        "4:00pm",
        "4:15pm",
        "4:30pm",
        "4:45pm",
        "5:00pm",
        "5:15pm",
        "5:30pm",
        "5:45pm",
        "6:00pm",
        "6:15pm",
        "6:30pm",
        "6:45pm",
        "7:00pm",
        "7:15pm",
        "7:30pm",
        "7:45pm",
        "8:00pm",
        "8:15pm",
        "8:30pm",
        "8:45pm",
        "9:00pm",
        "9:15pm",
        "9:30pm",
        "9:45pm",
        "10:00pm",
      ],
      durationOptions: [
        "15 Minutes",
        "30 Minutes",
        "45 Minutes",
        "1 Hour",
        "1 Hour & 15 Minutes",
        "1 Hour & 30 Minutes",
        "1 Hour & 45 Minutes",
        "2 Hours",
        "2 Hour & 15 Minutes",
        "2 Hour & 30 Minutes",
        "2 Hour & 45 Minutes",
        "3 Hours",
        "3 Hour & 15 Minutes",
        "3 Hour & 30 Minutes",
        "3 Hour & 45 Minutes",
        "4 Hours",
        "4 Hour & 15 Minutes",
        "4 Hour & 30 Minutes",
        "4 Hour & 45 Minutes",
        "5 Hours",
        "5 Hour & 15 Minutes",
        "5 Hour & 30 Minutes",
        "5 Hour & 45 Minutes",
        "6 Hours",
        "6 Hour & 15 Minutes",
        "6 Hour & 30 Minutes",
        "6 Hour & 45 Minutes",
        "All Afternoon",
        "All Day",
      ],
      // Driver quantity breakdown editing
      editDriverQuantityBreakdown: {
        looseYardsRegular: 0,
        looseYardsHeavy: 0,
        minisRegular: 0,
        minisHeavy: 0,
      },
      editDriverMaterialTypes: [],
    };
  },
  computed: {
    ...mapState(["user"]),
    isCongestionZone() {
      const postalCode = this.truckRequest && this.truckRequest.jobSite
        ? this.truckRequest.jobSite.postalCode
        : null;
      return isInCongestionZone(postalCode);
    },
    requestDateFormatted() {
      return this.truckRequest.requestDate
        ? moment(this.truckRequest.requestDate).format("YYYY-MM-DD")
        : "";
    },
    scheduledDateFormatted() {
      return this.truckRequest.scheduledDay &&
        this.truckRequest.scheduledDay.day
        ? moment(this.truckRequest.scheduledDay.day).format("MMMM D YYYY")
        : "";
    },
    pickupTypeOptions() {
      return this.truckRequest.requestType === "Roll Off"
        ? ["Swap", "Live Load", "Pickup", "Drop Off Container"]
        : ["Loose Yards", "Mini Containers", "Garbage Cans"];
    },
    // A breakdown only counts as real when it carries a number. Mirrors
    // hasQuantityBreakdown in functions/src/index.ts — an all-zero breakdown is
    // a leftover stamp, not driver data, and must not hide the closingQty row.
    hasQuantityBreakdown() {
      const breakdown = this.truckRequest.driverQuantityBreakdown;
      if (!breakdown) return false;
      return [
        breakdown.looseYardsRegular,
        breakdown.looseYardsHeavy,
        breakdown.minisRegular,
        breakdown.minisHeavy,
      ].some((v) => Number(v) > 0);
    },
  },
  async created() {
    await this.getDrivers();
    if (this.truckRequest.closeDate) {
      this.closeDateFormatted = moment(
        this.truckRequest.closeDate.toDate()
      ).format("YYYY-MM-DD");
    }
  },
  methods: {
    closeClicked() {
      this.$emit("close");
    },
    getClosingMiniPickupQty(tr) {
      // Use closing qty if available, otherwise fall back to original request
      // qty. Checked against null rather than truthiness: a confirmed 0 is a
      // real answer and must not fall back to the requested amount.
      if (tr.closingMiniPickupQty != null) {
        return Math.abs(tr.closingMiniPickupQty);
      }
      if (tr.miniPickupQty) {
        return Math.abs(tr.miniPickupQty);
      }
      return 0;
    },
    getClosingMiniDropoffQty(tr) {
      // See getClosingMiniPickupQty - a confirmed 0 must not fall back.
      if (tr.closingMiniDropoffQty != null) {
        return Math.abs(tr.closingMiniDropoffQty);
      }
      if (tr.miniDropoffQty) {
        return Math.abs(tr.miniDropoffQty);
      }
      return 0;
    },
    async getDrivers() {
      try {
        const resDrivers = await fb.db
          .collection("users")
          .where("role", "==", "Driver")
          .get();
        let drivers = [];
        resDrivers.forEach((d) => {
          drivers.push({
            id: d.id,
            ...d.data(),
          });
        });
        drivers = drivers.map((d) => {
          return { name: `${d.firstName} ${d.lastName}`, ...d };
        });
        drivers = sortBy(drivers, "name");
        this.drivers = drivers;
      } catch (error) {
        this.snack = true;
        this.snackColor = "error";
        this.snackText = "Error loading drivers";
      }
    },
    requestTypeChanged() {
      // Reset pickup type when request type changes
      this.truckRequest.pickupType = null;
    },
    updateScheduledDay() {
      // Update the display date when scheduled day changes
      const day = moment(this.truckRequest.scheduledDay.day);
      this.truckRequest.scheduledDay.displayDate = day.format("MMM Do (dddd)");
      this.truckRequest.scheduledDay.displayDate2 = day.format("M-D-YY");
    },
    updateCloseDate() {
      // Convert string date back to Firestore timestamp format
      const date = moment(this.closeDateFormatted).toDate();
      this.truckRequest.closeDate = {
        toDate: () => date,
        seconds: Math.floor(date.getTime() / 1000),
        nanoseconds: 0,
      };
    },
    startEditing() {
      // Initialize driver edit data with current values
      if (this.truckRequest.driverQuantityBreakdown) {
        this.editDriverQuantityBreakdown = {
          ...this.truckRequest.driverQuantityBreakdown,
        };
      } else {
        this.editDriverQuantityBreakdown = {
          looseYardsRegular: 0,
          looseYardsHeavy: 0,
          minisRegular: 0,
          minisHeavy: 0,
        };
      }

      if (this.truckRequest.driverMaterialTypes) {
        this.editDriverMaterialTypes = [
          ...this.truckRequest.driverMaterialTypes,
        ];
      } else {
        this.editDriverMaterialTypes = [];
      }

      this.editingTickets = true;
    },
    async saveTruckRequest() {
      try {
        // Prepare update object with only defined fields
        const updateData = {};

        // Request details - only add if they exist
        if (this.truckRequest.requestDate !== undefined)
          updateData.requestDate = this.truckRequest.requestDate;
        if (this.truckRequest.requestTime !== undefined)
          updateData.requestTime = this.truckRequest.requestTime;
        if (this.truckRequest.requestType !== undefined)
          updateData.requestType = this.truckRequest.requestType;
        if (this.truckRequest.pickupType !== undefined)
          updateData.pickupType = this.truckRequest.pickupType;
        // Material type excluded from editing
        if (this.truckRequest.qty !== undefined)
          updateData.qty = this.truckRequest.qty;
        if (this.truckRequest.duration !== undefined)
          updateData.duration = this.truckRequest.duration;
        if (this.truckRequest.notes !== undefined)
          updateData.notes = this.truckRequest.notes;

        // Dump tickets - always include if they exist
        if (this.truckRequest.dumpTickets) {
          updateData.dumpTickets = this.truckRequest.dumpTickets;
        }

        // Add scheduling details if they exist
        if (this.truckRequest.scheduledDay) {
          updateData.scheduledDay = this.truckRequest.scheduledDay;
          if (this.truckRequest.scheduledTime !== undefined)
            updateData.scheduledTime = this.truckRequest.scheduledTime;
          if (this.truckRequest.scheduledDuration !== undefined)
            updateData.scheduledDuration = this.truckRequest.scheduledDuration;
          if (this.truckRequest.scheduledDriver)
            updateData.scheduledDriver = this.truckRequest.scheduledDriver;
          if (this.truckRequest.truckManagerNotes !== undefined)
            updateData.truckManagerNotes = this.truckRequest.truckManagerNotes;
        }

        // Add closing details if they exist
        if (this.truckRequest.closeDate) {
          updateData.closeDate = this.truckRequest.closeDate;
          if (this.truckRequest.closingQty !== undefined)
            updateData.closingQty = this.truckRequest.closingQty;
          if (this.truckRequest.closeNotes !== undefined)
            updateData.closeNotes = this.truckRequest.closeNotes;
          if (this.truckRequest.clientPrintName !== undefined)
            updateData.clientPrintName = this.truckRequest.clientPrintName;
          if (this.truckRequest.closingMiniPickupQty !== undefined)
            updateData.closingMiniPickupQty = this.truckRequest.closingMiniPickupQty;
          if (this.truckRequest.closingMiniDropoffQty !== undefined)
            updateData.closingMiniDropoffQty = this.truckRequest.closingMiniDropoffQty;

          // Add new driver data if being edited. Packer only — matching the
          // close-out in views/manage/TruckRequest.vue. Roll Off and Mini
          // Pickup/Dropoff carry their quantity elsewhere (container size /
          // pickup & dropoff counts), so writing a breakdown for them stamps
          // an all-zero object that blanks the Dump Log container size.
          if (
            this.editDriverQuantityBreakdown &&
            this.truckRequest.requestType === "Packer"
          ) {
            updateData.driverQuantityBreakdown = this.editDriverQuantityBreakdown;
            updateData.driverMaterialTypes = this.editDriverMaterialTypes;

            // Calculate effective pickup type
            const hasLooseYards =
              (this.editDriverQuantityBreakdown.looseYardsRegular || 0) +
                (this.editDriverQuantityBreakdown.looseYardsHeavy || 0) >
              0;
            const hasMinis =
              (this.editDriverQuantityBreakdown.minisRegular || 0) +
                (this.editDriverQuantityBreakdown.minisHeavy || 0) >
              0;

            if (hasLooseYards && hasMinis) {
              updateData.driverEffectivePickupType = "Both";
            } else if (hasLooseYards) {
              updateData.driverEffectivePickupType = "Loose Yards";
            } else if (hasMinis) {
              updateData.driverEffectivePickupType = "Mini Containers";
            } else {
              updateData.driverEffectivePickupType = null;
            }
          }
        }

        // Update the truck request
        await fb.db
          .collection("truckRequests")
          .doc(this.truckRequest.orderNumber)
          .update(updateData);

        this.editingTickets = false;
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Truck request updated successfully";
      } catch (error) {
        console.error("Error updating truck request:", error);
        this.snack = true;
        this.snackColor = "error";
        this.snackText = "Error updating truck request";
      }
    },
  },
};
</script>

<style scoped>
.small-col {
  width: 60px;
}
@media (min-width: 600px) {
  .small-col {
    width: 90px;
  }
}
thead tr {
  background: #f3f3f3;
}
</style>
